/**
 * Add/edit dialog for a DIRECT routing rule - who new claims go to.
 *
 * Deliberately light: name, the shared assignee block (role picker +
 * strategy appear only for role targets), notify, active. The fallback
 * order itself is managed by dragging rows in the section list, not here.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/shared/components/ui/button'
import { Switch } from '@/shared/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm, type FormField } from '@/shared/lib/forms'
import {
  useCreateRoutingRule,
  useUpdateRoutingRule,
  type RoutingMeta,
  type RoutingRule,
  type RoutingStrategy,
} from '@/admin/hooks/use-routing-rules'
import {
  buildAssigneeFields,
  resolveAssigneeFromForm,
  useMemberOptions,
} from './routing-helpers'

interface DirectRuleDialogProps {
  editing: RoutingRule | null
  meta: RoutingMeta
  onClose: () => void
}

export function DirectRuleDialog({
  editing,
  meta,
  onClose,
}: DirectRuleDialogProps) {
  const { t } = useTranslation()
  const isEdit = editing !== null
  const create = useCreateRoutingRule()
  const update = useUpdateRoutingRule()
  const pending = create.isPending || update.isPending

  const personOptions = useMemberOptions()
  const roleOptions = useMemo(
    () => meta.roles.map((role) => ({ value: role.name, label: role.name })),
    [meta.roles],
  )
  const zoneRoleOptions = useMemo(
    () => (meta.zone_roles ?? []).map((r) => ({ value: r.name, label: r.name })),
    [meta.zone_roles],
  )
  const multiWorkflow = (meta.workflows ?? []).length > 1

  const fields = useMemo<FormField[]>(() => {
    const initialKind = editing?.assignee.kind ?? 'role'
    return [
      {
        type: 'text',
        name: 'name',
        label: 'Rule name',
        placeholder: 'e.g. New claims go to Claims Handlers',
        value: editing?.name ?? '',
        testId: 'routing-rule-name',
        validationSchema: z.string().trim().min(1, 'Name the rule.'),
      },
      ...(multiWorkflow
        ? ([
            {
              type: 'select',
              name: 'workflow_key',
              label: 'Claim workflow',
              value: editing?.workflow_key ?? '',
              testId: 'routing-workflow',
              options: [
                { label: 'All claim workflows', value: '' },
                ...(meta.workflows ?? []).map((w) => ({
                  label: w.label,
                  value: w.key,
                })),
              ],
            },
          ] as FormField[])
        : []),
      ...buildAssigneeFields({
        roleOptions,
        personOptions: personOptions ?? [],
        strategyOptions: meta.strategy_options,
        zoneRoleOptions,
        initial: {
          kind: initialKind,
          role: initialKind === 'role' ? (editing?.assignee.value ?? '') : '',
          person:
            initialKind === 'person' ? (editing?.assignee.value ?? '') : '',
          zoneRole:
            initialKind === 'zone' ? (editing?.assignee.value ?? '') : '',
          strategy: editing?.strategy || 'least_busy',
          prefer_creator: editing?.prefer_creator ?? false,
        },
      }),
      {
        type: 'checkbox',
        name: 'notify',
        label: 'Email the assignee a link to the claim',
        value: editing?.notify ?? true,
      },
      {
        type: 'checkbox',
        name: 'only_if_unassigned',
        label: t(
          'routing.rule.onlyIfUnassigned',
          'Only assign if the claim has no owner yet (never overwrite)',
        ),
        value: editing?.only_if_unassigned ?? false,
      },
      {
        type: 'custom',
        name: 'is_active',
        value: editing?.is_active ?? true,
        render: (formContext) => {
          const active = Boolean(formContext.values.is_active)
          return (
            <Button
              variant="ghost"
              type="button"
              onClick={() => formContext.setFieldValue('is_active', !active)}
              className="h-auto w-full justify-start gap-3 whitespace-normal rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-left hover:border-[var(--green-border)]"
            >
              <Switch checked={active} decorative />
              <span className="block text-[12px] text-[var(--ink)]">
                {active ? 'Rule active' : 'Rule paused'}
              </span>
            </Button>
          )
        },
      },
    ]
  }, [
    t,
    editing,
    roleOptions,
    personOptions,
    zoneRoleOptions,
    meta.strategy_options,
    meta.workflows,
    multiWorkflow,
  ])

  const { Form } = useForm(
    { name: 'direct-rule-form', submit: 'footer', fields },
    async (raw) => {
      const values = raw as Record<string, unknown>
      const { kind, value: target } = resolveAssigneeFromForm(values)
      if (!target) {
        toast.error(
          kind === 'role'
            ? 'Pick the role that receives new claims.'
            : kind === 'zone'
              ? 'Pick the zone-owner role.'
              : 'Pick the person who receives new claims.',
        )
        return
      }
      const payload = {
        kind: 'direct' as const,
        name: String(values.name).trim(),
        is_active: Boolean(values.is_active),
        assignee: { kind, value: target },
        strategy:
          kind === 'role'
            ? (String(values.strategy) as RoutingStrategy)
            : undefined,
        prefer_creator: kind === 'role' ? Boolean(values.prefer_creator) : false,
        notify: Boolean(values.notify),
        only_if_unassigned: Boolean(values.only_if_unassigned),
        workflow_key: multiWorkflow ? String(values.workflow_key ?? '') : '',
      }
      try {
        if (isEdit) {
          await update.mutateAsync({ uuid: editing.uuid, ...payload })
        } else {
          await create.mutateAsync(payload)
        }
        toast.success('Routing rule saved')
        onClose()
      } catch (err) {
        toastApiError(err, 'Failed to save the routing rule')
      }
    },
    [fields, editing, isEdit, multiWorkflow],
  )

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-[560px] sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit direct routing rule' : 'Add direct routing rule'}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            Applied when a claim is created. Rules are tried top to bottom -
            the first one that can assign someone wins.
          </DialogDescription>
        </DialogHeader>
        {personOptions ? (
          <Form className="space-y-5" />
        ) : (
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-[var(--ink-3)]" />
        )}
        <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="direct-rule-form"
            disabled={pending}
            data-testid="direct-rule-submit"
          >
            {pending ? 'Saving...' : 'Save rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
